import React, { useCallback, useState } from "react";
import {
  Platform,
  Linking,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import ButtonGradient from "../button-gradient";
import { button, FontFamily, FontWeight } from "../../styles/appStyles";
import { helpers } from "../../utils/helpers";
import isEmpty from "lodash/isEmpty";

const SUBSCRIBE_PATH = "/pricing/individual";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildSubscribeUrl(isLoggedIn, email) {
  if (isLoggedIn) {
    return SUBSCRIBE_PATH;
  }

  const params = [
    `redirect=${encodeURIComponent(SUBSCRIBE_PATH)}`,
    "providerHint=tmc-passwordless",
    `emailHint=${encodeURIComponent(email.trim())}`,
  ];

  return `/sign-in?${params.join("&")}`;
}

function getEmailValidationError(email) {
  const trimmed = email.trim();

  if (!trimmed) {
    return "Email is required";
  }

  if (!emailRegex.test(trimmed)) {
    return "Invalid email address";
  }

  return null;
}

const navigateToUrl = (url, props) => {
  if (props.navigateToScreen) {
    props.navigateToScreen(url);
    return;
  }

  if (props.navigateWeb) {
    props.navigateWeb(url);
    return;
  }

  if (helpers.isWeb() && typeof window !== "undefined") {
    window.location.href = url;
    return;
  }

  if (props.navigation?.navigate) {
    props.navigation.navigate(url);
    return;
  }

  Linking.openURL(url);
};

export default function NewsletterSection(props) {
  const { token, userProfile } = props;
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const { width } = useWindowDimensions();
  const isSm = width >= 640;
  const isMd = width >= 768;
  const isLg = width >= 1024;

  const handleSubmit = useCallback(() => {
    if (!token) {
      const validationError = getEmailValidationError(email);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setError("");
    navigateToUrl(buildSubscribeUrl(!!token, email), props);
  }, [email, props, token]);

  const isNonSubscribedUser =
    isEmpty(userProfile?.subscription) &&
    isEmpty(userProfile?.team_subscription);

  return (
    <View style={styles.section} accessibilityLabelledBy="newsletter-heading">
      <View style={[styles.container, isMd && styles.containerMd]}>
        <View
          style={[
            styles.content,
            isMd && styles.contentMd,
            isLg && styles.contentLg,
          ]}
        >
          <View style={styles.copy}>
            <Text
              nativeID="newsletter-heading"
              style={[styles.heading, isMd && styles.headingMd]}
            >
              Context is everything.
            </Text>

            <Text style={[styles.description, isMd && styles.descriptionMd]}>
              Detailed stories on technology startups, business and economic
              current affairs.
            </Text>
          </View>

          <View
            style={[
              styles.form,
              isSm && styles.formSm,
              isMd && styles.formMd,
              isLg && styles.formLg,
            ]}
          >
            {!token && (
              <View style={[styles.inputWrap, isSm && styles.inputWrapSm]}>
                <TextInput
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect={false}
                  inputMode="email"
                  keyboardType="email-address"
                  onChangeText={(value) => {
                    setEmail(value);
                    if (error) {
                      setError("");
                    }
                  }}
                  onSubmitEditing={handleSubmit}
                  placeholder="you@work.com"
                  placeholderTextColor="#A39C8E"
                  returnKeyType="go"
                  style={[styles.input, isSm && styles.inputSm]}
                  value={email}
                />
                {!!error && (
                  <Text accessibilityRole="alert" style={styles.error}>
                    {error}
                  </Text>
                )}
              </View>
            )}
            {isNonSubscribedUser && (
              <View style={[styles.buttonWrap, isSm && styles.buttonWrapSm]}>
                <ButtonGradient
                  title="SUBSCRIBE"
                  rootStyle={{
                    btnWrapper: [button.primaryGradient, styles.button],
                    btnText: [button.primaryGradientText, styles.buttonText],
                  }}
                  paddingOverride={true}
                  onClick={handleSubmit}
                  darkMode={props.darkMode}
                  isNewGradiant={true}
                  fullWidth={true}
                />
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    width: "100%",
    backgroundColor: "#efe7d5",
  },
  container: {
    width: "100%",
    maxWidth: 1440,
    paddingHorizontal: 24,
    paddingVertical: 48,
    alignSelf: "center",
  },
  containerMd: {
    paddingVertical: 56,
  },
  content: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 24,
  },
  contentMd: {
    gap: 40,
  },
  contentLg: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  copy: {
    maxWidth: 620,
  },
  heading: {
    color: "#24352E",
    fontFamily: FontFamily.interBold,
    fontSize: 26,
    fontWeight: FontWeight.bold,
    letterSpacing: 0.78,
    lineHeight: 28.6,
  },
  headingMd: {
    fontSize: 34,
    letterSpacing: 1.02,
    lineHeight: 37.4,
  },
  description: {
    color: "#8f897b",
    fontFamily: FontFamily.interRegular,
    fontSize: 14,
    fontWeight: FontWeight.regular,
    letterSpacing: 0.42,
    lineHeight: 21,
    marginTop: 10,
    maxWidth: 450,
  },
  descriptionMd: {
    fontSize: 15.36,
    letterSpacing: 0.46,
    lineHeight: 23.04,
  },
  form: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 8,
    width: "100%",
  },
  formSm: {
    flexDirection: "row",
  },
  formMd: {
    gap: 16,
  },
  formLg: {
    alignItems: "flex-start",
    width: "auto",
  },
  inputWrap: {
    width: "100%",
  },
  inputWrapSm: {
    width: 290,
  },
  input: {
    backgroundColor: "#fbf8f0",
    borderColor: "#e1d9c8",
    borderRadius: 2,
    borderWidth: 1,
    color: "#2c2a24",
    fontFamily: FontFamily.interRegular,
    fontSize: 14,
    height: 44,
    outlineStyle: Platform.OS === "web" ? "none" : undefined,
    paddingHorizontal: 24,
    width: "100%",
  },
  inputSm: {
    width: 290,
  },
  error: {
    color: "#ef4444",
    fontFamily: FontFamily.interRegular,
    fontSize: 14,
    fontWeight: FontWeight.regular,
    marginTop: 4,
    paddingLeft: 4,
  },
  button: {
    borderRadius: 2,
    height: 44,
    maxWidth: "100%",
    width: "100%",
  },
  buttonWrap: {
    flexShrink: 0,
    width: "100%",
  },
  buttonWrapSm: {
    width: 150,
  },
  buttonText: {
    fontFamily: FontFamily.interBold,
    fontSize: 14,
    fontWeight: FontWeight.bold,
    letterSpacing: 0.42,
    paddingHorizontal: 24,
    paddingVertical: 12,
    textTransform: "uppercase",
  },
});
